// LessonAudioPlayer.tsx — a calm, on-brand player for the AI-narrated version of
// a written lesson. Sits at the top of the reading view. Streams on demand
// (preload="none") so nothing downloads until the listener taps play.
import { useEffect, useRef, useState } from 'react';
import { Pressable } from '@/components/ui';
import { Icon } from '@/components/icons';

const fmt = (s: number) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

export function LessonAudioPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const [rate, setRate] = useState(1);

  // Reset when the lesson (src) changes.
  useEffect(() => {
    setPlaying(false);
    setLoading(false);
    setCur(0);
    setDur(0);
  }, [src]);

  // Apply the chosen playback speed (persists across lessons).
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  const RATES = [1, 1.25, 1.5, 2, 0.8];
  const cycleRate = () => setRate((r) => RATES[(RATES.indexOf(r) + 1) % RATES.length] ?? 1);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.playbackRate = rate;
      setLoading(true);
      void a.play().then(() => setLoading(false)).catch(() => setLoading(false));
    } else {
      a.pause();
    }
  };

  const seekFromEvent = (clientX: number) => {
    const a = audioRef.current;
    const el = trackRef.current;
    if (!a || !el || !dur) return;
    const rect = el.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    a.currentTime = frac * dur;
    setCur(a.currentTime);
  };

  const pct = dur ? Math.min(100, (cur / dur) * 100) : 0;

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
        border: '1px solid var(--hair)',
        borderRadius: 16,
        boxShadow: 'var(--edge), var(--sh1)',
        padding: '13px 15px 15px',
        marginBottom: 22,
      }}
    >
      {/* eyebrow — quiet "audio version available" cue */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          color: 'var(--amber-deep)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 11.5,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          marginBottom: 11,
        }}
      >
        <Icon name="headphones" size={15} /> Audio version available
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        {/* play / pause */}
        <Pressable
          as="button"
          onClick={toggle}
          ariaLabel={playing ? 'Pause narration' : 'Play narration'}
          style={{
            flexShrink: 0,
            width: 46,
            height: 46,
            borderRadius: 999,
            border: 'none',
            background: 'var(--amber-deep)',
            color: '#FBF1DE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(120,72,20,0.28)',
          }}
        >
          <Icon name={playing ? 'pause' : 'play'} size={19} />
        </Pressable>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* scrubbable progress track */}
          <div
            ref={trackRef}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(dur)}
            aria-valuenow={Math.round(cur)}
            tabIndex={0}
            onPointerDown={(e) => {
              (e.target as HTMLElement).setPointerCapture(e.pointerId);
              seekFromEvent(e.clientX);
            }}
            onPointerMove={(e) => {
              if (e.buttons === 1) seekFromEvent(e.clientX);
            }}
            style={{
              position: 'relative',
              height: 22,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              touchAction: 'none',
            }}
          >
            <div style={{ position: 'relative', width: '100%', height: 5, borderRadius: 999, background: 'rgba(120,72,20,0.16)' }}>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${pct}%`,
                  borderRadius: 999,
                  background: 'var(--amber-deep)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `${pct}%`,
                  top: '50%',
                  width: 13,
                  height: 13,
                  marginLeft: -6.5,
                  borderRadius: 999,
                  background: '#fff',
                  border: '2px solid var(--amber-deep)',
                  transform: 'translateY(-50%)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              />
            </div>
          </div>
          {/* times */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 3,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--amber-deep)',
              opacity: 0.8,
            }}
          >
            <span>{loading && !playing ? 'Loading…' : fmt(cur)}</span>
            <span>{dur ? fmt(dur) : '—:—'}</span>
          </div>
        </div>

        {/* playback speed — tap to cycle */}
        <Pressable
          as="button"
          onClick={cycleRate}
          ariaLabel={`Playback speed ${rate}×, tap to change`}
          style={{
            flexShrink: 0,
            minWidth: 44,
            height: 32,
            padding: '0 10px',
            borderRadius: 999,
            border: '1px solid var(--hair)',
            background: 'var(--card-top)',
            color: 'var(--amber-deep)',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 12.5,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {rate}×
        </Pressable>
      </div>

      <audio
        ref={audioRef}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onWaiting={() => setLoading(true)}
        onPlaying={() => setLoading(false)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration || 0)}
        onDurationChange={(e) => setDur(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCur(e.currentTarget.currentTime || 0)}
        onEnded={() => setPlaying(false)}
      />
    </div>
  );
}
